import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import {
  ElementPositionDto,
  ElementSizeDto,
  SceneTypeDto,
} from "@repo/api/overlays";
import { useCallback } from "react";
import ElementInput from "./ElementInput";
import { useElementMotionStore } from "@/lib/stores/element-motion-store/element-motion-store";

type PositionSliderInputProps = {
  fieldName: "x" | "y";
  element: { position: ElementPositionDto; externalId: string };
  sceneType: SceneTypeDto;
  sliderMax: number;
};

export function ElementPositionInput({
  fieldName,
  element,
}: PositionSliderInputProps) {
  const tsValue = useElementMotionStore(
    (state) => state.movingElements[element.externalId]?.[fieldName],
  );
  const updateElement = useEditorStore((state) => state.service.updateElement);

  const onChange = useCallback(
    (value: number) => {
      updateElement(element.externalId, (element) => {
        if (!("position" in element)) {
          throw new Error("Position is not in element");
        }

        element.position[fieldName] = value;
      });
    },
    [updateElement, fieldName, element],
  );

  const valueToUse = tsValue ?? element.position[fieldName];

  return (
    <ElementInput
      label={fieldName.toUpperCase()}
      value={Math.floor(valueToUse)}
      className="w-[140px]"
      onChange={onChange}
    />
  );
}

type SizeSliderInputProps = {
  fieldName: "w" | "h";
  element: { size: ElementSizeDto; externalId: string };
  aspectRatio: number | null;
  sceneType: SceneTypeDto;
  sliderMax: number;
};

export function ElementSizeInput({ fieldName, element }: SizeSliderInputProps) {
  const tsValue = useElementMotionStore((state) => {
    return state.movingElements[element.externalId]?.[fieldName];
  });

  const updateElement = useEditorStore((state) => state.service.updateElement);

  const onChange = useCallback(
    (value: number) => {
      updateElement(element.externalId, (element) => {
        if (!("size" in element)) {
          throw new Error("Size is not in element");
        }

        element.size = {
          ...element.size,
          [fieldName]: value,
        };
      });
    },
    [updateElement, fieldName, element],
  );

  const valueToUse = tsValue ?? element.size[fieldName];

  return (
    <ElementInput
      label={fieldName.toUpperCase()}
      value={Math.floor(valueToUse)}
      className="w-[140px]"
      onChange={onChange}
      allowNegativeValues={false}
    />
  );
}
